/**
 * Date formatting utilities with timezone awareness.
 *
 * All timestamps from the backend are in UTC (Unix timestamps).
 * This utility formats them in the user's local timezone and
 * provides the timezone abbreviation for display.
 */

/**
 * Get the user's current timezone abbreviation (e.g., "PST", "EST", "CET")
 */
export const getTimezoneAbbreviation = (): string => {
  const date = new Date();
  // Get the timezone name from Intl API
  const timezoneName = Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;

  return timezoneName || 'Local';
};

/**
 * Get the user's full timezone name (e.g., "America/Los_Angeles")
 */
export const getTimezoneFullName = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format a Unix timestamp (in seconds) to a localized date string.
 * Displays in the user's local timezone.
 *
 * @param timestamp - Unix timestamp in seconds
 * @param locale - Locale string (e.g., 'en', 'es')
 * @param options - Additional Intl.DateTimeFormat options
 * @returns Formatted date string in user's local timezone
 */
export const formatDateTime = (
  timestamp: number,
  locale: string = 'en',
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  };

  return new Date(timestamp * 1000).toLocaleString(locale, defaultOptions);
};

/**
 * Format a Unix timestamp with timezone indicator.
 * Returns both the formatted date and timezone abbreviation.
 *
 * @param timestamp - Unix timestamp in seconds
 * @param locale - Locale string (e.g., 'en', 'es')
 * @returns Object with formatted date and timezone
 */
export const formatDateTimeWithTimezone = (
  timestamp: number,
  locale: string = 'en'
): { formatted: string; timezone: string } => {
  return {
    formatted: formatDateTime(timestamp, locale),
    timezone: getTimezoneAbbreviation(),
  };
};

/**
 * Format a Unix timestamp to show date only (no time).
 *
 * @param timestamp - Unix timestamp in seconds
 * @param locale - Locale string (e.g., 'en', 'es')
 * @returns Formatted date string
 */
export const formatDateOnly = (timestamp: number, locale: string = 'en'): string => {
  return new Date(timestamp * 1000).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a Unix timestamp to show time only.
 *
 * @param timestamp - Unix timestamp in seconds
 * @param locale - Locale string (e.g., 'en', 'es')
 * @returns Formatted time string
 */
export const formatTimeOnly = (timestamp: number, locale: string = 'en'): string => {
  return new Date(timestamp * 1000).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  });
};
